import { motion } from "framer-motion";
import { PlayCircle, Users, MapPin } from "lucide-react";

const GOLDEN_SANDS =
  "https://customer-assets-0z36b82j.emergentagent.net/job_real-estate-varna/artifacts/56btsgs0_IMG_7472%202.webp";

export default function Community() {
  return (
    <section
      id="community"
      data-testid="community-section"
      className="relative bg-[#0F2647] text-[#F5EBD9] py-24 sm:py-32 lg:py-40 overflow-hidden border-t border-[#F5EBD9]/10"
    >
      <div className="absolute inset-0 sunset-glow opacity-40 pointer-events-none" />

      <div className="max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          className="mb-14 lg:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#F5892E]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#F5EBD9]/70">
                Общност · Иво Димовски
              </span>
            </div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-[22ch]">
              Гласът, който <span className="text-[#F5892E]">целият пазар</span> слуша.
            </h2>
          </div>
          <div className="md:max-w-md">
            <p className="text-lg text-[#F5EBD9]/80 leading-relaxed">
              С над <span className="font-display text-2xl text-[#F5EBD9]">170 000</span>{" "}
              последователи, Иво е един от най-популярните инвеститори в имоти в България.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-12 gap-6 lg:gap-10">
          {/* YouTube video */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1, ease: [0.2, 0.8, 0.2, 1] }}
            className="col-span-12 lg:col-span-7"
          >
            <div className="relative frame-shadow overflow-hidden bg-black aspect-video">
              <iframe
                data-testid="community-youtube"
                src="https://www.youtube.com/embed/DBwuV2j17OA?rel=0&modestbranding=1"
                title="Иво Димовски — Играта на Имотите"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            </div>
            <div className="mt-4 flex items-center gap-3 text-[#F5EBD9]/70">
              <PlayCircle className="w-4 h-4 text-[#F5892E]" strokeWidth={2} />
              <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.32em]">
                Гледай · Youtube
              </span>
            </div>
          </motion.div>

          {/* Golden Sands retreat */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1, delay: 0.1, ease: [0.2, 0.8, 0.2, 1] }}
            className="col-span-12 lg:col-span-5"
          >
            <div
              className="relative spotlight frame-shadow overflow-hidden aspect-[4/5] lg:aspect-auto lg:h-full"
              data-testid="community-golden-sands"
            >
              <img
                src={GOLDEN_SANDS}
                alt="Retreat Golden Sands 2026 — общността на Играта на Имотите"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050d1e]/85 via-[#050d1e]/15 to-transparent" />
              <div className="absolute top-4 right-4 bg-[#F5892E] text-[#0B1F3D] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.32em]">
                Retreat 2026
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#F5892E] mb-2">
                  Общността
                </div>
                <div className="font-display uppercase text-xl sm:text-2xl lg:text-3xl leading-tight">
                  Golden Sands 2026
                </div>
                <div className="mt-3 flex items-center gap-2 text-[#F5EBD9]/85">
                  <MapPin className="w-3.5 h-3.5 text-[#F5892E]" strokeWidth={2} />
                  <span className="font-mono text-[10px] uppercase tracking-widest">
                    Ежегоден retreat на общността
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Stats row */}
        <div className="mt-12 grid grid-cols-3 gap-6 sm:gap-10 border-t border-[#F5EBD9]/15 pt-10">
          {[
            { icon: Users, v: "170K+", l: "Последователи" },
            { icon: Users, v: "10K+", l: "Читатели на книгата" },
            { icon: Users, v: "500+", l: "Участници 2025" },
          ].map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              className="text-center sm:text-left"
            >
              <div className="font-display display-tight text-4xl sm:text-5xl lg:text-6xl text-[#F5892E]">
                {s.v}
              </div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.32em] text-[#F5EBD9]/60">
                {s.l}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
